/**
 * EVENTSFLO-PROFILE-PHOTO-SIGNED-URL-V1
 *
 * Tiny `<img>` wrapper that mints a signed profile-photo URL for the
 * given `staffId` and renders it. When `staffId` is falsy, or the
 * mint fails, or the image fails to load, renders `fallback`.
 *
 * Usage:
 *   <SignedStaffPhoto
 *     staffId={staff.id}
 *     profilePhotoUrl={staff.profile_photo_url}
 *     alt="Profile"
 *     className="w-full h-full object-cover rounded"
 *     fallback={<Camera className="h-8 w-8 opacity-50" />}
 *   />
 *
 * If `profilePhotoUrl` is set to a fully-qualified `http(s)://` URL
 * (e.g. a stub external image), it's used as-is.
 *
 * `refreshKey` forces re-minting after upload/crop/delete actions.
 */
import { useState } from 'react';
import { useSignedPhotoUrl } from './photoSignedUrl';

export function SignedStaffPhoto({
  staffId,
  profilePhotoUrl,
  alt = 'Profile',
  className,
  fallback = null,
  refreshKey = 0,
  dataTestId,
}) {
  const [errored, setErrored] = useState(false);

  // Honour fully-qualified external URLs verbatim — BUT not URLs
  // pointing at our own legacy `/api/staff/{id}/photo` endpoint.
  // Many existing `staff.profile_photo_url` rows are stored as
  // absolute URLs like
  //   https://<backend>/api/staff/{id}/photo?t=...
  // which we MUST route through the signed-URL flow (otherwise the
  // bare legacy URL 401s now that the legacy route is gated).
  const looksLikeLegacyStaffPhoto = (u) => (
    typeof u === 'string' && /\/api\/staff\/[^/]+\/photo(\?|$)/.test(u)
  );
  const isExternal = typeof profilePhotoUrl === 'string'
    && /^https?:\/\//i.test(profilePhotoUrl)
    && !looksLikeLegacyStaffPhoto(profilePhotoUrl);

  // Treat empty string as "no photo".
  const hasNoPhotoPointer = !profilePhotoUrl;

  // Route through the signed-URL minter whenever:
  //  - we have a staffId AND
  //  - the URL is missing OR points at our own legacy photo endpoint.
  const useSignedFlow = staffId && (
    hasNoPhotoPointer || !isExternal
  );

  const signedUrl = useSignedPhotoUrl(
    useSignedFlow ? staffId : null,
    refreshKey,
  );

  const src = isExternal ? profilePhotoUrl : signedUrl;
  if (!src || errored) return fallback;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErrored(true)}
      data-testid={dataTestId}
    />
  );
}
